import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isSearchTooShort, SEARCH_TOO_SHORT_HINT } from '@/lib/search'

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value: string
  onChange: (value: string) => void
  /** Trieda obalu — šírka/flex v lište filtrov. */
  className?: string
  inputClassName?: string
  /** Menšie pole (text-sm, menšia lupa). */
  compact?: boolean
  /** Doplnok vpravo v poli, napr. tlačidlo na vymazanie. */
  children?: ReactNode
}

/**
 * Vyhľadávacie pole adminu: lupa, bez automatického vypĺňania správcov hesiel
 * a pri príliš krátkom texte nápoveda — hľadá sa až od SEARCH_MIN_LENGTH znakov
 * (lib/search.ts). Hodnotu na dotaz dá useSearchTerm / useUrlSearch (hooks/use-search.ts).
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onChange, className, inputClassName, compact = false, children, type = 'search', ...rest },
  ref,
) {
  return (
    <div className={cn('relative', className)}>
      <Search
        size={compact ? 14 : 15}
        className={cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-400', compact ? 'left-2.5' : 'left-3')}
      />
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn('input', compact ? 'pl-8 text-sm' : 'pl-9', inputClassName)}
        autoComplete="off"
        data-1p-ignore="true"
        data-lpignore="true"
        data-form-type="other"
        {...rest}
      />
      {isSearchTooShort(value) && (
        // Vpravo je pri type="search" natívny krížik, inak prípadný doplnok (children).
        <span
          className={cn(
            'pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-xs text-gray-400',
            type === 'search' || children ? 'right-8' : 'right-3',
          )}
        >
          {SEARCH_TOO_SHORT_HINT}
        </span>
      )}
      {children}
    </div>
  )
})
